'use client';

import React, { useEffect, useRef, useState } from 'react';
import { ContactAPI } from '../api/api';
import { apiPost } from '../utils/utils';
import { notify } from '../utils/notify';
import ContactCtaSection from './contact/ContactCtaSection';
import ContactFormSection from './contact/ContactFormSection';
import ContactHeroSection from './contact/ContactHeroSection';
import ContactHoursSection from './contact/ContactHoursSection';
import ContactInfoSection from './contact/ContactInfoSection';
import ContactSocialSection from './contact/ContactSocialSection';
import {
  businessHours,
  contactInfo,
  heroTrustChips,
  services,
  socialLinks,
  trustPoints,
} from './contact/contactData';

const ContactUs = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    website: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.15 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name } = e.target;
    const value = name === 'phone' ? e.target.value.replace(/\D/g, '').slice(0, 10) : e.target.value;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formState.name.trim()) newErrors.name = 'Full name is required';

    if (!formState.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formState.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    if (!formState.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formState.phone)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }

    if (!formState.service.trim()) newErrors.service = 'Please select a service';
    if (!formState.message.trim()) newErrors.message = 'Message is required';

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    try {
      setIsSubmitting(true);
      await apiPost(ContactAPI, formState);

      await notify({
        icon: 'success',
        title: 'Message Sent!',
        text: 'We will get back to you within 30 minutes.',
        confirmButtonColor: '#FF6F00',
      });

      setFormState({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
        website: '',
      });
      setErrors({});
    } catch (error) {
      await notify({
        icon: 'error',
        title: 'Oops...',
        text: 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyles = (field: keyof typeof formState) => ({
    borderColor: errors[field] ? '#EF4444' : 'rgba(30, 144, 255, 0.16)',
    background: '#F8FAFF',
  });

  return (
    <>
      <ContactHeroSection data-component-id="src/components/ContactUs.tsx:128:6" data-component-path="src/components/ContactUs.tsx" data-component-line="128" data-component-end-line="128" data-component-file="ContactUs.tsx" data-component-name="ContactHeroSection" data-component-content="%7B%22elementName%22%3A%22ContactHeroSection%22%7D" contactInfo={contactInfo} heroTrustChips={heroTrustChips} />

      <div data-component-id="src/components/ContactUs.tsx:130:6" data-component-path="src/components/ContactUs.tsx" data-component-line="130" data-component-end-line="144" data-component-file="ContactUs.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" ref={sectionRef}>
        <ContactInfoSection data-component-id="src/components/ContactUs.tsx:131:8" data-component-path="src/components/ContactUs.tsx" data-component-line="131" data-component-end-line="131" data-component-file="ContactUs.tsx" data-component-name="ContactInfoSection" data-component-content="%7B%22elementName%22%3A%22ContactInfoSection%22%7D" contactInfo={contactInfo} />
        <ContactHoursSection data-component-id="src/components/ContactUs.tsx:132:8" data-component-path="src/components/ContactUs.tsx" data-component-line="132" data-component-end-line="132" data-component-file="ContactUs.tsx" data-component-name="ContactHoursSection" data-component-content="%7B%22elementName%22%3A%22ContactHoursSection%22%7D" businessHours={businessHours} trustPoints={trustPoints} />
        <ContactFormSection data-component-id="src/components/ContactUs.tsx:133:8" data-component-path="src/components/ContactUs.tsx" data-component-line="133" data-component-end-line="141" data-component-file="ContactUs.tsx" data-component-name="ContactFormSection" data-component-content="%7B%22elementName%22%3A%22ContactFormSection%22%7D"
          formState={formState}
          errors={errors}
          isSubmitting={isSubmitting}
          services={services}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
          inputStyles={inputStyles}
        />
        <ContactSocialSection data-component-id="src/components/ContactUs.tsx:142:8" data-component-path="src/components/ContactUs.tsx" data-component-line="142" data-component-end-line="142" data-component-file="ContactUs.tsx" data-component-name="ContactSocialSection" data-component-content="%7B%22elementName%22%3A%22ContactSocialSection%22%7D" socialLinks={socialLinks} />
        <ContactCtaSection data-component-id="src/components/ContactUs.tsx:143:8" data-component-path="src/components/ContactUs.tsx" data-component-line="143" data-component-end-line="143" data-component-file="ContactUs.tsx" data-component-name="ContactCtaSection" data-component-content="%7B%22elementName%22%3A%22ContactCtaSection%22%7D" />
      </div>
    </>
  );
};

export default ContactUs;
